// Pure helpers of the automation builder: the flow it draws from a saved automation, the edits to
// its steps, and what a new automation starts with

const DEFAULT_STEPS = {
  email: () => ({
    type: "email",
    config: { subject: "", preheader: "", body: "", marketing: false },
  }),
  delay: () => ({ type: "delay", config: { amount: 1, unit: "days" } }),
  filter: () => ({ type: "filter", config: { filters: {} } }),
};

/** A new step of the given type, with what it starts with. */
export const defaultStep = (type) => DEFAULT_STEPS[type]();

const plainStep = ({ type, config }) => ({ type, config });

/** The steps as the API takes them (no positions). */
export const toApiSteps = (steps) => steps.map(plainStep);

/** The steps with a new one at `index`. */
export const insertStep = (steps, index, step) => [
  ...steps.slice(0, index),
  step,
  ...steps.slice(index),
];

export const removeStep = (steps, index) =>
  steps.filter((_, position) => position !== index);

/** A copy of the step right after it. */
export const duplicateStep = (steps, index) =>
  insertStep(steps, index + 1, structuredClone(plainStep(steps[index])));

/** The step moved from `from` to the slot `to` of the list as it was. */
export const moveStep = (steps, from, to) => {
  if (from === to || from + 1 === to) return steps;
  const moving = steps[from];
  const rest = removeStep(steps, from);
  return insertStep(rest, to > from ? to - 1 : to, moving);
};

/** The steps with the config of one of them replaced. */
export const updateStepConfig = (steps, index, config) =>
  steps.map((step, position) =>
    position === index
      ? { ...step, config: { ...step.config, ...config } }
      : step
  );

/** Whether a filters object narrows anything. */
export const hasFilters = (filters) =>
  Object.values(filters ?? {}).some((values) => values.length > 0);

/**
 * What the canvas draws, top to bottom: the trigger, the filter of the trigger (when it has one),
 * the steps and the exit rules. `slot` is where a step added with the "+" below the node goes.
 * @returns {Array<{kind: string, key: string, index?: number, step?: object, slot?: number}>}
 */
export const buildFlow = ({ automation, steps }) => {
  const nodes = [{ kind: "trigger", key: "trigger" }];
  if (hasFilters(automation.filters)) {
    nodes.push({ kind: "triggerFilter", key: "trigger-filter", slot: 0 });
  }
  steps.forEach((step, index) => {
    nodes.push({
      kind: step.type,
      key: `step-${index}`,
      index,
      step,
      slot: index + 1,
    });
  });
  nodes.push({ kind: "exit", key: "exit" });
  return nodes;
};

/**
 * The minutes-and-hours line of a delay, or null when it has no valid amount.
 */
export const isValidDelay = ({ amount, unit }) =>
  Number.isInteger(amount) && amount >= 1 && ["hours", "days"].includes(unit);

// What a new automation starts with: the exit rules and the re-entry of its product
const PRODUCT_DEFAULTS = {
  push: {
    exitRules: ["cancelled", "access_ended"],
    reentry: "once_per_purchase",
  },
  press: {
    exitRules: ["cancelled", "access_ended"],
    reentry: "once_per_purchase",
  },
  pull: { exitRules: ["stopped_training"], reentry: "never" },
  perform: { exitRules: ["access_removed"], reentry: "never" },
};

export const defaultsForProduct = (product) => {
  const { exitRules, reentry } = PRODUCT_DEFAULTS[product] ?? {
    exitRules: [],
    reentry: "never",
  };
  return { exitRules: exitRules.map((event) => ({ event })), reentry };
};

const SOURCE_LABELS = { stripe: "stripe", app: "app", scheduler: "scheduler" };

/** The key of the label that says where a trigger's events come from. */
export const sourceLabelKey = (source) => SOURCE_LABELS[source] ?? "app";

/**
 * The place of a step in the sequence for the "Immediately" label: the first email, with nothing
 * waiting before it, goes out as soon as the trigger fires.
 */
export const sendsImmediately = (steps, index) =>
  steps[index]?.type === "email" &&
  steps.slice(0, index).every((step) => step.type === "filter");

/** The wait right before an email (what "Send Timing" shows and edits), or null when it has none. */
export const findSendDelay = (steps, index) => {
  const before = steps[index - 1];
  return before?.type === "delay" ? before.config : null;
};

/**
 * The steps with the wait before the email at `index` set to `delay` (`{amount, unit}`), or taken
 * away when it is null, and the place the email has afterwards (a wait added before it pushes it
 * one step on, one removed pulls it back).
 */
export const applySendDelay = (steps, index, delay) => {
  const hasDelay = findSendDelay(steps, index) !== null;
  if (!delay) {
    return hasDelay
      ? { steps: removeStep(steps, index - 1), index: index - 1 }
      : { steps, index };
  }
  if (hasDelay) {
    return {
      steps: updateStepConfig(steps, index - 1, {
        amount: delay.amount,
        unit: delay.unit,
      }),
      index,
    };
  }
  return {
    steps: insertStep(steps, index, { type: "delay", config: delay }),
    index: index + 1,
  };
};

const MERGE_TOKEN = /\{\{\s*([a-z_]+)\s*\}\}/gi;
const MERGE_SPAN = /<span data-merge="([a-z_]+)"[^>]*>[^<]*<\/span>/g;

const escapeHtml = (text) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const mergeSpan = (key) => `<span data-merge="${key.toLowerCase()}"></span>`;

/** The subject or preheader (plain text with `{{fields}}`) as the editor's content. */
export const plainToEditorHtml = (text) =>
  `<p>${escapeHtml(text ?? "").replace(MERGE_TOKEN, (_match, key) => mergeSpan(key))}</p>`;

/**
 * The saved body as the editor's content: the HTML it wrote before, or text and templates that
 * carry bare `{{fields}}`. Either way each field becomes one chip.
 */
export const bodyToEditorHtml = (body) => {
  const text = body ?? "";
  if (!text.trim()) return "";
  const withChips = (html) =>
    html
      .replace(MERGE_SPAN, (_match, key) => mergeSpan(key))
      .replace(MERGE_TOKEN, (_match, key) => mergeSpan(key));
  return /<[a-z][^>]*>/i.test(text) ? withChips(text) : plainToEditorHtml(text);
};

/** The issues of the publish check that belong to one step, as a list of codes. */
export const issueCodesOf = (issues, position) =>
  (issues ?? [])
    .filter((issue) => issue.stepPosition === position)
    .map((issue) => issue.code);

/** The step of an email panel: its saved content with the edits applied. */
export const emailConfig = (saved, edits) => ({
  ...saved,
  subject: edits.subject.trim(),
  preheader: edits.preheader.trim(),
  body: edits.body,
  marketing: edits.marketing,
});
